import { scopedChannelApi } from '@/session/scopedClients';
import type { RequestScope } from '@/session/scopedTransport';
import type { ChannelApiResponse } from '@/types/endpoints';
import { utils } from '@/utils';
import type { ReadRequestOptions } from '../../requestOptions';
import type { ExpansionProductRow, ExpansionProductStatus, ExpansionRung } from './types';

/**
 * The server's own cap on `per_page` (`ExpansionController::MAX_PER_PAGE`). Asking for more is not
 * an error there — it silently answers this many — so the client refuses it here instead, and a
 * reader that pages through a whole result set asks for exactly this.
 */
export const RUN_PRODUCTS_MAX_PER_PAGE = 200;

const validate = (runId: number, subscriptionId: number, page?: number, perPage?: number) => {
  if (!runId || runId < 1) {
    throw new Error('Valid run ID is required');
  }
  if (!subscriptionId || subscriptionId < 1) {
    throw new Error('Valid subscription ID is required');
  }
  if (page !== undefined && (!page || page < 1)) {
    throw new Error('page must be a positive number');
  }
  if (perPage !== undefined && (!perPage || perPage < 1 || perPage > RUN_PRODUCTS_MAX_PER_PAGE)) {
    throw new Error(`perPage must be between 1 and ${RUN_PRODUCTS_MAX_PER_PAGE}`);
  }
};

type RequestParams = {
  runId: number;
  /**
   * Any subscription of the organization — the API authorises the caller against it and scopes
   * every read by organization, never by this id.
   */
  subscriptionId: number;
  page?: number;
  perPage?: number;
  status?: ExpansionProductStatus;
  key?: string;
  rung?: ExpansionRung;
  q?: string;
  familyId?: number;
};

export type GetRunProductsResponse = {
  data: ExpansionProductRow[];
  total: number;
};

export const getRunProducts = async (
  scope: RequestScope,
  { runId, subscriptionId, page, perPage, status, key, rung, q, familyId }: RequestParams,
  options: ReadRequestOptions
): Promise<ChannelApiResponse<GetRunProductsResponse>> => {
  try {
    validate(runId, subscriptionId, page, perPage);

    const result = await scopedChannelApi(scope).get<GetRunProductsResponse>(`atts/expansion/runs/${runId}/products`, {
      ...options,
      headers: {
        'x-astur-subscription-id': subscriptionId.toString(),
      },
      params: {
        page,
        per_page: perPage,
        status,
        key,
        rung,
        q,
        family_id: familyId,
      },
    });

    return {
      data: result.data,
      status: result.status,
    };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
};
